import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import IconWrapper from 'components/common/IconWrapper/IconWrapper'
import MainLogo from 'components/assets/icons/MainLogo'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import Input from 'components/common/Input/Input'
import InputError from 'components/common/InputError/InputError'
import Button from 'components/common/Button/Button'
import EyeOpen from 'components/assets/icons/EyeOpen'
import EyeClosed from 'components/assets/icons/EyeClosed'
import Loader from 'components/assets/icons/Loader'
import Link from 'components/common/Link/Link'
import { emailValidation, passwordValidation, confirmPasswordValidation } from 'constants/inputValidation'
import api from 'helpers/api'
import style from '../Auth.module.scss'
import commonStyle from '../../../Wrapper/wrapper.module.scss'

const SignupReporting = () => {
  const navigate = useNavigate()
  const { t, i18n: { language } } = useTranslation()
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [isConfirmShown, setIsConfirmShown] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [loading, setLoading] = useState(false)

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    watch,
    trigger
  } = useForm({
    criteriaMode: 'all',
    mode: 'onTouched',
    delayError: 0,
    values: {
      email: '',
      password: '',
      confirm: ''
    },
  })

  useEffect(() => {
    trigger()
  }, [language, trigger])

  const onSubmit = handleSubmit(async data => {

    if (data.password !== data.confirm) {
      setErrorMessage(t('reporting:auth.password.differents'))
      return
    }

    setLoading(true)

    const register = async () => {
      try {
        const res = await api.post({ url: '/auth/reporting/signup', data })

        if (res.success && res.response.confirmToken) {
          navigate('/reporting/signup-confirm?token='+res.response.confirmToken)
        } else {
          setErrorMessage('failed')
        }
      } catch (error) {
        setErrorMessage(error.message)
        if (error.message === 'Email is already registered'){
          setTimeout( () => {
            navigate('/reporting/signin')
          }, 5000)
        }
      }
      setLoading(false)
    }
    setTimeout( () => register(), 500)
  })

  const messageSelection = () => {
    let message = ''
    switch (errorMessage){
      case 'Email is already registered':
        message = t('main:auth.signUp.knownEmail')
        break;
      case 'Partner does not exist':
        message = t('main:auth.signUp.unknownEmail')
        break;
      case 'Password should contain at least 9 characters, including 1 digit and 1 capital letter':
        message = t('main:auth.signUp.wrongPassword')
        break;
      case 'Too many requests':
        message = t('main:auth.confirm.tooManyRequests')
        break;
      case 'Unauthorized':
        message = t('main:auth.signIn.unauthorized')
        break;
      default:
        message = t('main:errorMessages.general')
        break;
    }
    return message
  }

  return (
    <section className={style.content}>
      <IconWrapper icon={MainLogo} className={style.logo} styles={{ fill: '#000093' }} text='Logo' />
      <h1 className={style.title}>{t('reporting:signup.title')}</h1>

      {errorMessage &&
        <ResponseMessage
          className={style.message}
          label={messageSelection()}
        />
      }

      <form onSubmit={onSubmit}>
        <Input
          register={register('email', emailValidation(t))}
          label={t('reporting:auth.email.label')+'*'}
          autocomplete="email"
          error={errors.firstname}
          className={commonStyle.input}
        />

        <Input
          register={register('password', passwordValidation(t))}
          label={t('reporting:auth.password.label')}
          type={isPasswordShown ? 'text' : 'password'}
          className={commonStyle.input}
          error={errors.password}
          suffix={
            isPasswordShown ? (
              <EyeClosed className={classNames(style.eyeClosed, 'clickable')} />
            ) : (
              <EyeOpen className={classNames(style.eyeOpen, 'clickable')} />
            )
          }
          onSuffixClick={() => setIsPasswordShown(!isPasswordShown)}
        />
        {errors.password &&
          <div className={style.error}>
            <p>{t('main:auth.password.required')}</p>
            {Object.values(errors.password.types).map( (error, id) => (
              <span key={'err'+id}>
                {error}
              </span>
            ))}
          </div>
        }

        <Input
          register={register('confirm', confirmPasswordValidation(t, watch('password')))}
          label={t('reporting:signup.confirm.label')}
          type={isConfirmShown ? 'text' : 'password'}
          className={commonStyle.input}
          error={errors.confirm}
          suffix={
            isConfirmShown ? (
              <EyeClosed className={classNames(style.eyeClosed, 'clickable')} />
            ) : (
              <EyeOpen className={classNames(style.eyeOpen, 'clickable')} />
            )
          }
          onSuffixClick={() => setIsConfirmShown(!isConfirmShown)}
        />
        {errors.confirm && <InputError error={errors.confirm} />}

        <Link
          className={style.link}
          label={t('main:auth.signUp.alreadyHaveAccount')}
          to={'/reporting/signin'}
        />

        <Button
          type={loading && 'loading'}
          className={style.button}
          label={loading ? <Loader/> : t('reporting:signup.button')}
          onClick={handleSubmit}
          disabled={!isValid || loading} 
        />
      </form>
    </section>
  )
}

export default SignupReporting
